import {
  Button,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CircleCheck } from 'tabler-icons-react';
import { z } from 'zod';

import { customAxios } from 'lib/axios';

import AuthLayout from './AuthLayout';

type ApiResponse = {
  success: boolean;
  message?: string;
};

function RequestAccessForm() {
  const { t } = useTranslation();
  const [isSubmitted, setSubmitted] = useState(false);

  const requestSchema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.firstNameShort') }),
        lastName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.lastNameShort') }),
        email: z.string().email({ message: t('auth.validation.validEmail') }),
        company: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.companyRequired') }),
        pitch: z
          .string()
          .trim()
          .min(10, { message: t('auth.validation.pitchMin') }),
      }),
    [t]
  );

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      pitch: '',
    },
    validate: zodResolver(requestSchema),
  });

  const handleSubmit = async (): Promise<void> => {
    try {
      const response = await customAxios.post<ApiResponse>(
        '/request-access/add',
        form.values,
      );
      if (response.data.success) {
        setSubmitted(true);
        showNotification({
          title: t('auth.requestAccess.notificationTitle'),
          message: t('auth.requestAccess.notificationBody'),
          color: 'teal',
        });
        form.reset();
      }
    } catch (error) {
      // Error handled by global handler
    }
  };

  if (isSubmitted) {
    return (
      <AuthLayout
        title="Request Received"
        subtitle={t('auth.requestAccess.successSubtitle')}
      >
        <Stack spacing="lg" align="center" py="md">
          <ThemeIcon
            size={64}
            radius="xl"
            variant="gradient"
            gradient={{ from: 'teal', to: 'cyan' }}
          >
            <CircleCheck size={36} />
          </ThemeIcon>
          <Text ta="center" c="dimmed" size="sm">
            {t('auth.requestAccess.thanks')}
          </Text>
          <Button
            variant="subtle"
            color="violet"
            radius="md"
            onClick={() => setSubmitted(false)}
          >
            {t('auth.requestAccess.submitAnother')}
          </Button>
          <Text size="sm" c="dimmed">
            {t('auth.requestAccess.or')}
            {' '}
            <Text
              component={Link}
              to="/login"
              size="sm"
              weight={600}
              color="violet.6"
              inherit
            >
              {t('common.signIn')}
            </Text>
          </Text>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Request Access"
      subtitle={t('auth.requestAccess.intro')}
    >
      <form
        onSubmit={form.onSubmit(() => {
          // eslint-disable-next-line no-void
          void handleSubmit();
        })}
      >
        <Stack spacing="md">
          <SimpleGrid
            cols={2}
            breakpoints={[{ maxWidth: 'xs', cols: 1 }]}
          >
            <TextInput
              label={t('auth.requestAccess.firstName')}
              placeholder="Jane"
              radius="md"
              size="md"
              {...form.getInputProps('firstName')}
            />
            <TextInput
              label={t('auth.requestAccess.lastName')}
              placeholder="Doe"
              radius="md"
              size="md"
              {...form.getInputProps('lastName')}
            />
          </SimpleGrid>

          <TextInput
            label={t('auth.requestAccess.workEmail')}
            placeholder={t('auth.login.emailPlaceholder')}
            radius="md"
            size="md"
            {...form.getInputProps('email')}
          />

          <TextInput
            label={t('auth.requestAccess.company')}
            placeholder="Acme Inc."
            radius="md"
            size="md"
            {...form.getInputProps('company')}
          />

          <Textarea
            label={t('auth.requestAccess.pitch')}
            placeholder="Tell us what you're building..."
            minRows={3}
            radius="md"
            size="md"
            {...form.getInputProps('pitch')}
          />

          <Button
            type="submit"
            fullWidth
            size="md"
            radius="md"
            variant="gradient"
            gradient={{ from: 'indigo', to: 'violet' }}
            sx={{
              boxShadow:
                '0 4px 14px rgba(99,102,241,0.3)',
            }}
          >
            {t('auth.requestAccess.submit')}
          </Button>
        </Stack>
      </form>

      <Text size="sm" c="dimmed" ta="center" mt="lg">
        {t('auth.register.alreadyHaveAccount')}
        {' '}
        <Text
          component={Link}
          to="/login"
          size="sm"
          weight={600}
          color="violet.6"
          inherit
        >
          {t('common.signIn')}
        </Text>
      </Text>
    </AuthLayout>
  );
}

export default RequestAccessForm;
